import React, { useState, useEffect, useCallback } from "react";
import {
	Stack,
	Typography,
	Button,
	Grid,
	Tab,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	useTheme,
	useMediaQuery,
	Box,
} from "@mui/material";
import { Menu } from "@mantine/core";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { CardContainerRetro } from "../../components/Container/CardContainerRetro";
import { CardPost } from "../Card/CardPost";
import { Section } from "../../components/Container/Section";
import { SectionTitleWithExp } from "../../components/customTypo/SectionTitleWithExp";
import colorsCustom from "../../designTokens/colorsCustom";
import { TopPerformanceModal } from "./TopPerformanceModal";
import { useTranslation } from "react-i18next";
import { trackEvent } from "../../utils/analytics";
import { useTopPerformanceData } from "../../hooks/useThreadsSelector";
import { CardGridSkeleton } from "../skeleton/CardGridSkeleton";
import {
	IconDownload,
	IconChevronDown,
	IconFileCode,
} from "@tabler/icons-react";

export const TopPerformanceThreadsSection = () => {
	const { t } = useTranslation();
	const theme = useTheme();

	// 반응형 Hook
	const isMobile = useMediaQuery(theme.breakpoints.down("xl"));

	// ThreadsContext에서 상위 성과 스레드 데이터 가져오기
	const { lifetimeThreadsList, isThreadsLoading, isDataReady, error } =
		useTopPerformanceData();
	const [showAllViews, setShowAllViews] = useState(false);
	const [showAllEngagementRate, setShowAllEngagementRate] = useState(false);
	const [showAllEngagementAmount, setShowAllEngagementAmount] = useState(false);
	const [tabValue, setTabValue] = useState("views");

	// 기간 필터 상태
	const [dateFilter, setDateFilter] = useState("all");
	const [customStartMonth, setCustomStartMonth] = useState(null);
	const [customEndMonth, setCustomEndMonth] = useState(null);

	// 다운로드 메뉴 상태
	const [downloadMenuOpened, setDownloadMenuOpened] = useState(false);

	// 필터링된 스레드 데이터
	const [topViewsThreads, setTopViewsThreads] = useState([]);
	const [topEngagementRateThreads, setTopEngagementRateThreads] = useState([]);
	const [topEngagementAmountThreads, setTopEngagementAmountThreads] = useState(
		[]
	);

	// 기간 필터 옵션
	const dateFilterOptions = [
		{ value: "all", label: t("filters.period.all") },
		{ value: "30days", label: t("filters.period.30days") },
		{ value: "90days", label: t("filters.period.90days") },
		{ value: "1year", label: t("filters.period.1year") },
		{ value: "custom", label: t("filters.period.custom") },
	];

	// 날짜 필터링 함수
	const filterThreadsByDate = useCallback(
		(threads, filter) => {
			if (!threads || threads.length === 0) return [];

			if (filter === "all") return threads;

			const now = new Date();
			let startDate, endDate;

			switch (filter) {
				case "30days":
					startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
					break;
				case "90days":
					startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
					break;
				case "1year":
					startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
					break;
				case "custom":
					if (!customStartMonth || !customEndMonth) return threads;
					// 선택된 월의 첫날과 마지막날 계산
					startDate = new Date(
						customStartMonth.getFullYear(),
						customStartMonth.getMonth(),
						1
					);
					endDate = new Date(
						customEndMonth.getFullYear(),
						customEndMonth.getMonth() + 1,
						0,
						23,
						59,
						59
					);
					break;
				default:
					return threads;
			}

			return threads.filter((thread) => {
				if (!thread.timestamp) return false;
				const threadDate = new Date(thread.timestamp);
				return threadDate >= startDate && (!endDate || threadDate <= endDate);
			});
		},
		[customStartMonth, customEndMonth]
	);

	// 필터링된 데이터로 top threads 계산
	useEffect(() => {
		if (!lifetimeThreadsList || lifetimeThreadsList.length === 0) return;

		const filteredThreads = filterThreadsByDate(
			lifetimeThreadsList,
			dateFilter
		);

		if (filteredThreads.length === 0) {
			setTopViewsThreads([]);
			setTopEngagementRateThreads([]);
			setTopEngagementAmountThreads([]);
			return;
		}

		// Sort threadsList by date
		const sortedThreadsList = [...filteredThreads].sort(
			(a, b) => new Date(a.timestamp) - new Date(b.timestamp)
		);

		// Sort by views and get top 20
		const listSize = 20;
		const sortedByViews = [...sortedThreadsList]
			.sort((a, b) => b.views - a.views)
			.slice(0, listSize);

		// Calculate average views
		const totalViews = filteredThreads.reduce((sum, thread) => {
			return sum + (typeof thread.views === "number" ? thread.views : 0);
		}, 0);
		const averageViews = totalViews / filteredThreads.length;

		// Sort by engagement rate and get top 20 for threads with above-average views
		const sortedByEngagementRate = [...filteredThreads]
			.filter((thread) => thread.views > averageViews)
			.map((thread) => ({
				...thread,
				engagementRate:
					thread.views > 0
						? (thread.likes + 2 * thread.replies + 3 * thread.reposts) /
						  thread.views
						: 0,
			}))
			.sort((a, b) => b.engagementRate - a.engagementRate)
			.slice(0, listSize);

		// Sort by engagement amount and get top 20
		const sortedByEngagementAmount = [...filteredThreads]
			.map((thread) => ({
				...thread,
				engagementAmount:
					thread.likes + 2 * thread.replies + 3 * thread.reposts,
			}))
			.sort((a, b) => b.engagementAmount - a.engagementAmount)
			.slice(0, listSize);

		// Create sets for each list (top 10 only)
		const upperLimit = 10;
		const viewsSet = new Set(
			sortedByViews.slice(0, upperLimit).map((thread) => thread.id)
		);
		const engagementRateSet = new Set(
			sortedByEngagementRate.slice(0, upperLimit).map((thread) => thread.id)
		);
		const engagementAmountSet = new Set(
			sortedByEngagementAmount.slice(0, upperLimit).map((thread) => thread.id)
		);

		// Function to check if a thread is in more than one top 10 list
		const isRemarkable = (threadId) => {
			let listCount = 0;
			if (viewsSet.has(threadId)) listCount++;
			if (engagementRateSet.has(threadId)) listCount++;
			if (engagementAmountSet.has(threadId)) listCount++;

			return listCount > 1;
		};

		// Add isRemarkable flag to threads
		const addRemarkableFlag = (list) =>
			list.map((thread) => ({
				...thread,
				isRemarkable: isRemarkable(thread.id),
			}));

		// Apply the remarkable flag to all lists
		setTopViewsThreads(addRemarkableFlag(sortedByViews));
		setTopEngagementRateThreads(addRemarkableFlag(sortedByEngagementRate));
		setTopEngagementAmountThreads(addRemarkableFlag(sortedByEngagementAmount));
	}, [
		lifetimeThreadsList,
		dateFilter,
		customStartMonth,
		customEndMonth,
		filterThreadsByDate,
	]);

	// 필터 변경 핸들러
	const handleDateFilterChange = (value) => {
		setDateFilter(value);
		if (value !== "custom") {
			setCustomStartMonth(null);
			setCustomEndMonth(null);
		}
	};

	// Top 100 JSON 다운로드 함수
	const handleDownloadTop100JSON = () => {
		if (!lifetimeThreadsList || lifetimeThreadsList.length === 0) {
			console.log("No data to download.");
			return;
		}

		const filteredThreads = filterThreadsByDate(
			lifetimeThreadsList,
			dateFilter
		);

		if (filteredThreads.length === 0) {
			console.log("No filtered data to download.");
			return;
		}

		// Calculate average views for engagement rate filtering
		const totalViews = filteredThreads.reduce((sum, thread) => {
			return sum + (typeof thread.views === "number" ? thread.views : 0);
		}, 0);
		const averageViews = totalViews / filteredThreads.length;

		// Top 100 by Views
		const top100Views = [...filteredThreads]
			.sort((a, b) => b.views - a.views)
			.slice(0, 100)
			.map((thread, index) => ({
				rank: index + 1,
				category: "views",
				date: new Date(thread.timestamp).toLocaleDateString(),
				content: thread.text.replace(/\uFFFD/g, ""),
				views: thread.views || 0,
				likes: thread.likes || 0,
				replies: thread.replies || 0,
				reposts: thread.reposts || 0,
				engagementRate:
					thread.views > 0
						? parseFloat(
								(
									((thread.likes + thread.replies + thread.reposts) /
										thread.views) *
									100
								).toFixed(2)
						  )
						: 0,
				engagementAmount:
					(thread.likes || 0) + (thread.replies || 0) + (thread.reposts || 0),
				link: thread.permalink || "",
			}));

		// Top 100 by Engagement Rate (above average views only)
		const top100EngagementRate = [...filteredThreads]
			.filter((thread) => thread.views > averageViews)
			.map((thread) => ({
				...thread,
				engagementRate:
					thread.views > 0
						? (thread.likes + 2 * thread.replies + 3 * thread.reposts) /
						  thread.views
						: 0,
			}))
			.sort((a, b) => b.engagementRate - a.engagementRate)
			.slice(0, 100)
			.map((thread, index) => ({
				rank: index + 1,
				category: "engagementRate",
				date: new Date(thread.timestamp).toLocaleDateString(),
				content: thread.text.replace(/\uFFFD/g, ""),
				views: thread.views || 0,
				likes: thread.likes || 0,
				replies: thread.replies || 0,
				reposts: thread.reposts || 0,
				engagementRate: parseFloat((thread.engagementRate * 100).toFixed(2)),
				engagementAmount:
					(thread.likes || 0) + (thread.replies || 0) + (thread.reposts || 0),
				link: thread.permalink || "",
			}));

		// Top 100 by Engagement Amount
		const top100EngagementAmount = [...filteredThreads]
			.map((thread) => ({
				...thread,
				engagementAmount:
					thread.likes + 2 * thread.replies + 3 * thread.reposts,
			}))
			.sort((a, b) => b.engagementAmount - a.engagementAmount)
			.slice(0, 100)
			.map((thread, index) => ({
				rank: index + 1,
				category: "engagementAmount",
				date: new Date(thread.timestamp).toLocaleDateString(),
				content: thread.text.replace(/\uFFFD/g, ""),
				views: thread.views || 0,
				likes: thread.likes || 0,
				replies: thread.replies || 0,
				reposts: thread.reposts || 0,
				engagementRate:
					thread.views > 0
						? parseFloat(
								(
									((thread.likes + thread.replies + thread.reposts) /
										thread.views) *
									100
								).toFixed(2)
						  )
						: 0,
				engagementAmount: thread.engagementAmount,
				link: thread.permalink || "",
			}));

		// 전체 데이터 구성
		const exportData = {
			metadata: {
				exportDate: new Date().toISOString(),
				dateFilter: dateFilter,
				customDateRange:
					dateFilter === "custom"
						? {
								startMonth: customStartMonth?.toISOString(),
								endMonth: customEndMonth?.toISOString(),
						  }
						: null,
				totalThreadsInPeriod: filteredThreads.length,
				averageViews: Math.round(averageViews),
			},
			topViews: top100Views,
			topEngagementRate: top100EngagementRate,
			topEngagementAmount: top100EngagementAmount,
		};

		// JSON 다운로드
		const jsonString = JSON.stringify(exportData, null, 2);
		const BOM = "\uFEFF";
		const blob = new Blob([BOM + jsonString], {
			type: "application/json;charset=utf-8;",
		});
		const link = document.createElement("a");
		const url = URL.createObjectURL(blob);
		link.setAttribute("href", url);

		// 파일명에 기간 정보 포함
		const periodText =
			dateFilter === "all"
				? "all-time"
				: dateFilter === "custom" && customStartMonth && customEndMonth
				? `${customStartMonth.getFullYear()}-${String(
						customStartMonth.getMonth() + 1
				  ).padStart(2, "0")}_to_${customEndMonth.getFullYear()}-${String(
						customEndMonth.getMonth() + 1
				  ).padStart(2, "0")}`
				: dateFilter;
		link.setAttribute("download", `top-performance-threads_${periodText}.json`);
		link.style.visibility = "hidden";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);

		trackEvent("download_top_performance_json", {
			dateFilter: dateFilter,
			totalThreads: filteredThreads.length,
			categories: 3,
		});
		console.log("Top 100 Performance JSON download triggered.");
		setDownloadMenuOpened(false);
	};

	// 에러 처리 - 기본값과 함께 에러 상태 표시
	if (error && lifetimeThreadsList.length === 0) {
		return (
			<Section>
				<SectionTitleWithExp
					title={t("lifetime.topPerformanceThreads.title")}
				/>
				<CardContainerRetro
					p={isMobile ? 16 : 24}
					bg={colorsCustom.paper}
					weight="bold"
					rd={16}
				>
					<Stack alignItems="center" spacing={2}>
						<Typography color="warning.main" textAlign="center" variant="body2">
							⚠️ {t("error.top_performance_threads")}
						</Typography>
						<Typography textAlign="center" color="text.disabled">
							{t("empty.top_performance_threads")}
						</Typography>
					</Stack>
				</CardContainerRetro>
			</Section>
		);
	}

	// 로딩 중
	if (isThreadsLoading || !isDataReady) {
		return (
			<CardGridSkeleton title={t("lifetime.topPerformanceThreads.title")} />
		);
	}

	// 로딩 중
	if (isThreadsLoading || !isDataReady) {
		return (
			<CardGridSkeleton title={t("lifetime.topPerformanceThreads.title")} />
		);
	}

	// 데이터 로딩 실패
	if (error && (!lifetimeThreadsList || lifetimeThreadsList.length === 0)) {
		return (
			<Section>
				<SectionTitleWithExp
					title={t("lifetime.topPerformanceThreads.title")}
				/>
				<CardContainerRetro
					p={isMobile ? "24px" : "32px"}
					bg={colorsCustom.paper}
					weight="bold"
					rd={16}
				>
					<Stack alignItems="center" spacing={2}>
						<Typography color="warning.main" textAlign="center" variant="body2">
							⚠️ {t("error.performanceDataFailed")}
						</Typography>
						<Typography textAlign="center" color="text.disabled">
							{t("error.threadsDataLoadFailed")}
						</Typography>
					</Stack>
				</CardContainerRetro>
			</Section>
		);
	}

	const renderThreadList = (
		threads,
		showAll,
		setShowAll,
		title,
		description
	) => (
		<CardContainerRetro
			p={isMobile ? "16px" : "24px"}
			bg={colorsCustom.paper}
			weight="bold"
			rd={16}
		>
			<Stack spacing={2} width="100%">
				<Typography variant={isMobile ? "h6" : "h6"} fontWeight={900}>
					{title}
				</Typography>
				{/* <Typography>{description}</Typography> */}
				{threads.slice(0, 4).map((thread) => (
					<CardPost
						key={thread.id}
						date={thread.timestamp}
						thread={thread}
						borderType="bottom"
						size={isMobile ? "lg" : "xl"}
						isRemarkable={thread.isRemarkable}
						rd={0}
						onClick={() =>
							trackEvent("Top_Performance_Threads_On_List", { metric: title })
						}
					/>
				))}
			</Stack>
		</CardContainerRetro>
	);

	return (
		<Section>
			<Stack spacing={2}>
				<SectionTitleWithExp
					title={t("lifetime.topPerformanceThreads.title")}
					exp={t("lifetime.topPerformanceThreads.description")}
				/>

				{/* 기간 필터 및 전체보기 버튼 */}
				<Stack
					direction={isMobile ? "column" : "row"}
					justifyContent="space-between"
					alignItems={isMobile ? "stretch" : "center"}
					spacing={1.5}
				>
					{/* 왼쪽: 날짜 설정 */}
					<Stack
						direction={isMobile ? "column" : "row"}
						alignItems={isMobile ? "stretch" : "center"}
						spacing={1.5}
					>
						<FormControl
							size={isMobile ? "small" : "medium"}
							sx={{ minWidth: isMobile ? "100%" : 200 }}
						>
							<InputLabel>{t("filters.period.selectPeriod")}</InputLabel>
							<Select
								value={dateFilter}
								onChange={(e) => handleDateFilterChange(e.target.value)}
								label={t("filters.period.selectPeriod")}
								sx={{
									backgroundColor: colorsCustom.paper,
									"& .MuiOutlinedInput-notchedOutline": {
										borderColor: colorsCustom.border,
									},
								}}
							>
								{dateFilterOptions.map((option) => (
									<MenuItem key={option.value} value={option.value}>
										{option.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>

						{dateFilter === "custom" && (
							<LocalizationProvider dateAdapter={AdapterDayjs}>
								<Stack
									direction={isMobile ? "column" : "row"}
									spacing={1}
									alignItems={isMobile ? "stretch" : "center"}
								>
									<Typography
										variant="body2"
										color="text.disabled"
										sx={{ whiteSpace: "nowrap" }}
									>
										{t("filters.period.fromTo")}
									</Typography>
									<DatePicker
										views={["year", "month"]}
										label={t("filters.period.startMonth")}
										value={customStartMonth}
										onChange={setCustomStartMonth}
										maxDate={customEndMonth || new Date()}
										slotProps={{
											textField: {
												size: isMobile ? "small" : "medium",
												sx: {
													minWidth: isMobile ? "100%" : 140,
													backgroundColor: colorsCustom.paper,
													"& .MuiOutlinedInput-notchedOutline": {
														borderColor: colorsCustom.border,
													},
												},
											},
										}}
									/>
									<Typography variant="body2" color="text.disabled">
										~
									</Typography>
									<DatePicker
										views={["year", "month"]}
										label={t("filters.period.endMonth")}
										value={customEndMonth}
										onChange={setCustomEndMonth}
										minDate={customStartMonth}
										maxDate={new Date()}
										slotProps={{
											textField: {
												size: isMobile ? "small" : "medium",
												sx: {
													minWidth: isMobile ? "100%" : 140,
													backgroundColor: colorsCustom.paper,
													"& .MuiOutlinedInput-notchedOutline": {
														borderColor: colorsCustom.border,
													},
												},
											},
										}}
									/>
								</Stack>
							</LocalizationProvider>
						)}
					</Stack>

					{/* 오른쪽: 전체보기 및 다운로드 버튼 */}
					<Stack direction={isMobile ? "column" : "row"} spacing={1.5}>
						{/* 다운로드 버튼 */}
						<Menu
							opened={downloadMenuOpened}
							onClose={() => setDownloadMenuOpened(false)}
							position="bottom-end"
							shadow="md"
							width={200}
						>
							<Menu.Target>
								<Button
									variant="outlined"
									color="secondary"
									size={isMobile ? "small" : "medium"}
									onClick={() => setDownloadMenuOpened(true)}
									disabled={
										!lifetimeThreadsList || lifetimeThreadsList.length === 0
									}
									sx={{
										backgroundColor: colorsCustom.paper,
										"& .MuiButton-startIcon": {
											marginRight: 0.5,
										},
										"& .MuiButton-endIcon": {
											marginLeft: 0.5,
										},
									}}
									startIcon={<IconDownload size={16} />}
									endIcon={<IconChevronDown size={14} />}
								>
									Top 100
								</Button>
							</Menu.Target>
							<Menu.Dropdown>
								<Menu.Item
									leftSection={<IconFileCode size={16} />}
									onClick={handleDownloadTop100JSON}
								>
									{t("searchThreads.terminology.downloadJson")} (Top 100)
								</Menu.Item>
							</Menu.Dropdown>
						</Menu>

						{/* 전체보기 버튼 */}
						{!isMobile && (
							<Button
								variant="outlined"
								color="primary"
								size="medium"
								onClick={() => {
									setShowAllViews(true);
									setShowAllEngagementRate(true);
									setShowAllEngagementAmount(true);
									trackEvent("See_All_Top_Performance_Threads");
								}}
							>
								{t("filters.period.viewAll")}
							</Button>
						)}
					</Stack>
				</Stack>
			</Stack>

			<Grid container spacing={isMobile ? 0 : 2}>
				{isMobile ? (
					<Grid size={{ xs: 12 }}>
						<TabContext value={tabValue || "views"}>
							<Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
								<TabList onChange={(e, newValue) => setTabValue(newValue)}>
									<Tab
										label={t("lifetime.topPerformanceThreads.topViews.title")}
										value="views"
									/>
									<Tab
										label={t(
											"lifetime.topPerformanceThreads.topEngagementRate.title"
										)}
										value="rate"
									/>
									<Tab
										label={t(
											"lifetime.topPerformanceThreads.topEngagementAmount.title"
										)}
										value="amount"
									/>
								</TabList>
							</Box>

							<TabPanel value="views" sx={{ p: 0 }}>
								{renderThreadList(
									topViewsThreads,
									showAllViews,
									setShowAllViews,
									t("lifetime.topPerformanceThreads.topViews.title"),
									t("lifetime.topPerformanceThreads.topViews.description")
								)}
							</TabPanel>

							<TabPanel value="rate" sx={{ p: 0 }}>
								{renderThreadList(
									topEngagementRateThreads,
									showAllEngagementRate,
									setShowAllEngagementRate,
									t("lifetime.topPerformanceThreads.topEngagementRate.title"),
									t(
										"lifetime.topPerformanceThreads.topEngagementRate.description"
									)
								)}
							</TabPanel>

							<TabPanel value="amount" sx={{ p: 0 }}>
								{renderThreadList(
									topEngagementAmountThreads,
									showAllEngagementAmount,
									setShowAllEngagementAmount,
									t("lifetime.topPerformanceThreads.topEngagementAmount.title"),
									t(
										"lifetime.topPerformanceThreads.topEngagementAmount.description"
									)
								)}
							</TabPanel>
						</TabContext>
					</Grid>
				) : (
					<>
						<Grid size={{ xs: 12, xl: 4 }}>
							{renderThreadList(
								topViewsThreads,
								showAllViews,
								setShowAllViews,
								t("lifetime.topPerformanceThreads.topViews.title"),
								t("lifetime.topPerformanceThreads.topViews.description")
							)}
						</Grid>
						<Grid size={{ xs: 12, xl: 4 }}>
							{renderThreadList(
								topEngagementRateThreads,
								showAllEngagementRate,
								setShowAllEngagementRate,
								t("lifetime.topPerformanceThreads.topEngagementRate.title"),
								t(
									"lifetime.topPerformanceThreads.topEngagementRate.description"
								)
							)}
						</Grid>
						<Grid size={{ xs: 12, xl: 4 }}>
							{renderThreadList(
								topEngagementAmountThreads,
								showAllEngagementAmount,
								setShowAllEngagementAmount,
								t("lifetime.topPerformanceThreads.topEngagementAmount.title"),
								t(
									"lifetime.topPerformanceThreads.topEngagementAmount.description"
								)
							)}
						</Grid>
					</>
				)}
			</Grid>
			<TopPerformanceModal
				open={showAllViews || showAllEngagementRate || showAllEngagementAmount}
				onClose={() => {
					setShowAllViews(false);
					setShowAllEngagementRate(false);
					setShowAllEngagementAmount(false);
				}}
				topViewsThreads={topViewsThreads}
				topEngagementRateThreads={topEngagementRateThreads}
				topEngagementAmountThreads={topEngagementAmountThreads}
			/>
		</Section>
	);
};
